import React, { useContext, useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import PropTypes from 'prop-types';

import { RULE_TYPES, TRANSACTION_TYPES } from '~/components/AddRuleDrawer/constants';
import { nameLabel } from '~/components/Autocomplete/Library';
import Button from '~/components/Button';
import { CurrencyField } from '~/components/CurrencyField';
import { PersonContext } from '~/contexts/PersonContext';
import {
  getAccountOptions,
  getBalanceSheetAccountOptions,
  padSubaccounts,
  subaccountSort,
} from '~/helpers/utils/functionalAutocompleteLibrary';

export default function FilterRulesDialog({ filter, updateFilter, closeDialog, isOpen }) {
  const { organizationId } = useContext(PersonContext);
  const [filterState, setFilterState] = useState(filter);
  const [analysisAccountOptions, setAnalysisAccountOptions] = useState([]);
  const [destinationAccountOptions, setDestinationAccountOptions] = useState([]);
  const typeOptions = [
    { name: TRANSACTION_TYPES.EXPENSE, id: TRANSACTION_TYPES.EXPENSE },
    { name: TRANSACTION_TYPES.REVENUE, id: TRANSACTION_TYPES.REVENUE },
    { name: TRANSACTION_TYPES.FIXED_ASSET_PURCHASE, id: TRANSACTION_TYPES.FIXED_ASSET_PURCHASE },
    { name: TRANSACTION_TYPES.TRANSFER_TO, id: TRANSACTION_TYPES.TRANSFER_TO },
    { name: TRANSACTION_TYPES.TRANSFER_FROM, id: TRANSACTION_TYPES.TRANSFER_FROM },
    { name: RULE_TYPES.IGNORE, id: RULE_TYPES.IGNORE },
    { name: RULE_TYPES.SPLIT, id: RULE_TYPES.SPLIT },
  ];

  const handleUpdateFilter = async (event) => {
    event.preventDefault();
    updateFilter({ ...filterState, page: 0 });
    closeDialog();
  };

  useEffect(() => {
    const getAccounts = async () => {
      const [analysisAccountOptions, destinationAccountOptions] = await Promise.allSettled([
        getAccountOptions(organizationId, {
          type2: ['Bank', 'Credit Card'],
          inactive: { $or: [null, false] },
        }),
        getBalanceSheetAccountOptions(organizationId, {
          type: ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'],
        }),
      ]);
      setAnalysisAccountOptions(analysisAccountOptions.value);
      setDestinationAccountOptions(subaccountSort(destinationAccountOptions.value));
    };
    getAccounts();
  }, [organizationId]);

  return (
    <Dialog open={isOpen} onClose={closeDialog} scroll="body" maxWidth="sm" fullWidth>
      <form onSubmit={handleUpdateFilter}>
        <DialogTitle>Filter Rules</DialogTitle>
        <DialogContent>
          <TextField
            label="Description Contains"
            fullWidth
            value={filterState.descriptionContains}
            onChange={(event) => {
              setFilterState({ ...filterState, descriptionContains: event.target.value });
            }}
            margin="dense"
          />
          <Autocomplete
            options={typeOptions}
            getOptionLabel={nameLabel}
            value={filterState.type}
            onChange={(_event, newValue) => {
              setFilterState({ ...filterState, type: newValue });
            }}
            renderInput={(params) => <TextField {...params} label="Type" placeholder="Type to Search" margin="dense" />}
          />
          <Autocomplete
            options={analysisAccountOptions}
            getOptionLabel={nameLabel}
            value={filterState.analysisAccount}
            onChange={(_event, newValue) => {
              setFilterState({ ...filterState, analysisAccount: newValue });
            }}
            renderInput={(params) => (
              <TextField {...params} label="Imported From Account" placeholder="Type to Search" margin="dense" />
            )}
          />
          <Autocomplete
            options={subaccountSort(destinationAccountOptions)}
            getOptionLabel={nameLabel}
            value={filterState.destinationAccount}
            onChange={(_event, newValue) => {
              setFilterState({ ...filterState, destinationAccount: newValue });
            }}
            renderOption={padSubaccounts}
            renderInput={(params) => (
              <TextField {...params} label="Booked To Account" placeholder="Type to Search" margin="dense" />
            )}
          />
          <CurrencyField
            fullWidth
            margin="dense"
            label="Amount"
            value={filterState.amount}
            onChange={(event) => {
              setFilterState({ ...filterState, amount: Number(event.target.value) });
            }}
          />
        </DialogContent>
        <DialogActions style={{ marginTop: 16 }}>
          <Button variant="contained" color="primary" size="medium" type="submit">
            Update Filter
          </Button>
          <Button
            variant="text"
            color="primary"
            size="medium"
            onClick={() => {
              closeDialog();
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

FilterRulesDialog.propTypes = {
  filter: PropTypes.object.isRequired,
  updateFilter: PropTypes.func.isRequired,
  closeDialog: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};
